// Pagina admin /encounters/:id: split-view trascript + dati strutturati.
// Sprint 4: aggiunte azioni review/clinical/notes + download PDF.

import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DogEarCard,
  RuleBicolor,
  OriginalText,
  MissingTranslationBadge,
  SplitButton,
} from '@audiq/ui';
import {
  pickMainText,
  pickOriginalText,
  needsTranslationButMissing,
  hasAnyOriginalAvailable,
} from '@audiq/shared';
import { useShowOriginal } from '../hooks/useShowOriginal';
import {
  api,
  downloadEncounterPdfUrl,
  type EncounterDetail,
  type EncounterPdfMode,
} from '../lib/api';

async function downloadPdf(id: string, mode: EncounterPdfMode = 'it'): Promise<void> {
  const url = downloadEncounterPdfUrl(id, mode);
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error('pdf_download_failed');
  const blob = await res.blob();
  const objUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objUrl;
  a.download = `audiq-encounter-${id.slice(0, 8)}-${mode}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objUrl);
}

export function EncounterDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [notes, setNotes] = useState('');
  const [notesDirty, setNotesDirty] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [showOriginal, setShowOriginal] = useShowOriginal();

  const query = useQuery({
    queryKey: ['encounter', id],
    queryFn: () => (id ? api.getEncounter(id) : Promise.reject(new Error('no id'))),
    enabled: !!id,
  });

  useEffect(() => {
    if (query.data) {
      setNotes(query.data.encounter.operator_notes ?? '');
      setNotesDirty(false);
    }
  }, [query.data]);

  const reviewMut = useMutation({
    mutationFn: () => api.markEncounterReviewed(id!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['encounter', id] }),
  });

  const clinicalMut = useMutation({
    mutationFn: (val: boolean) => api.markEncounterClinical(id!, val),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['encounter', id] }),
  });

  const notesMut = useMutation({
    mutationFn: () => api.updateEncounterNotes(id!, notes),
    onSuccess: () => {
      setNotesDirty(false);
      queryClient.invalidateQueries({ queryKey: ['encounter', id] });
    },
  });

  if (query.isLoading) {
    return (
      <div className="mx-auto max-w-6xl">
        <p style={{ color: 'var(--mute)' }}>Carico…</p>
      </div>
    );
  }
  if (query.error || !query.data) {
    return (
      <div className="mx-auto max-w-6xl">
        <p style={{ color: 'var(--danger)' }}>Errore nel caricamento dell'encounter.</p>
        <Link to="/encounters" className="mono underline" style={{ color: 'var(--mute)' }}>
          ← Torna alla lista
        </Link>
      </div>
    );
  }

  const detail: EncounterDetail = query.data;
  const { encounter, messages, captured_data, flag_events, outcome_results } = detail;

  const encounterLang = encounter.language_detected ?? null;

  const hasOriginalAvailable = hasAnyOriginalAvailable({
    messages: messages.map((m) => ({
      content: m.content ?? '',
      content_translation_it: m.content_translation_it ?? null,
    })),
    captured: captured_data.map((c) => ({
      value:
        typeof c.captured_value === 'string' ? c.captured_value : JSON.stringify(c.captured_value),
      translation_it: c.captured_value_translation_it?.text ?? null,
    })),
  });

  async function handleDownload(mode: EncounterPdfMode) {
    if (!id) return;
    setPdfError(null);
    setPdfBusy(true);
    try {
      await downloadPdf(id, mode);
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : 'errore');
    } finally {
      setPdfBusy(false);
    }
  }

  function fmt(ts?: string | null) {
    if (!ts) return '—';
    try {
      return new Date(ts).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'medium' });
    } catch {
      return ts;
    }
  }

  // Group captured by tags
  const capturedByTag = new Map<string, typeof captured_data>();
  for (const c of captured_data) {
    const tag = c.question_tags[0] ?? 'altro';
    if (!capturedByTag.has(tag)) capturedByTag.set(tag, []);
    capturedByTag.get(tag)!.push(c);
  }

  return (
    <div className="mx-auto max-w-6xl">
      <div className="mb-2 mono text-xs" style={{ color: 'var(--mute)' }}>
        <Link to="/encounters" className="underline">
          ← Conversazioni
        </Link>
      </div>
      <h2 className="serif text-2xl" style={{ color: 'var(--ink)' }}>
        {encounter.pseudonym}
      </h2>
      <div className="mt-1 flex flex-wrap gap-3 mono text-xs" style={{ color: 'var(--mute)' }}>
        <span>{fmt(encounter.started_at)}</span>
        <span>·</span>
        <span>turni: {encounter.total_turns}</span>
        <span>·</span>
        <span>completeness: {encounter.completeness_pct}%</span>
        {encounter.language_detected ? (
          <>
            <span>·</span>
            <span>
              {encounter.language_detected} ({encounter.language_script ?? '?'})
            </span>
          </>
        ) : null}
        {encounter.flow ? (
          <>
            <span>·</span>
            <span>
              flow: {encounter.flow.name} v{encounter.flow.version_number}
            </span>
          </>
        ) : null}
      </div>

      <RuleBicolor className="my-5" />

      {encounter.emergency_triggered ? (
        <div
          className="mb-5 rounded-md border-l-4 px-4 py-3"
          style={{
            background: 'var(--paper)',
            borderColor: 'var(--danger)',
            color: 'var(--danger)',
          }}
        >
          <strong>Emergenza rilevata:</strong> {encounter.emergency_keyword ?? '(LLM-side)'}
        </div>
      ) : null}

      {/* Azioni operatore (Sprint 4) */}
      <div
        className="mb-5 flex flex-wrap items-center gap-2 rounded-md border p-3"
        style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
      >
        <button
          type="button"
          disabled={!!encounter.reviewed_at || reviewMut.isPending}
          onClick={() => reviewMut.mutate()}
          className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-50"
          style={{
            borderColor: encounter.reviewed_at ? 'var(--primary)' : 'var(--line)',
            color: encounter.reviewed_at ? 'var(--primary)' : 'var(--ink)',
          }}
        >
          {encounter.reviewed_at
            ? `✓ rivisto · ${fmt(encounter.reviewed_at)}`
            : reviewMut.isPending
              ? 'segno…'
              : 'Marca come rivisto'}
        </button>
        <button
          type="button"
          disabled={clinicalMut.isPending}
          onClick={() => clinicalMut.mutate(!encounter.marked_clinical_relevant)}
          className="rounded-md border px-3 py-1.5 text-sm disabled:opacity-50"
          style={{
            borderColor: encounter.marked_clinical_relevant ? 'var(--accent)' : 'var(--line)',
            color: encounter.marked_clinical_relevant ? 'var(--accent)' : 'var(--ink)',
          }}
        >
          {encounter.marked_clinical_relevant ? '★ clinicamente rilevante' : 'Segna come rilevante'}
        </button>
        <div className="flex items-center gap-3">
          {hasOriginalAvailable ? (
            <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--mute)' }}>
              <input
                type="checkbox"
                checked={showOriginal}
                onChange={(e) => setShowOriginal(e.target.checked)}
              />
              Mostra originale
            </label>
          ) : null}
          <SplitButton
            label="⬇ Scarica PDF report"
            onPrimaryClick={() => handleDownload('it')}
            options={
              hasOriginalAvailable
                ? [
                    { label: 'Solo italiano', onClick: () => handleDownload('it') },
                    {
                      label: 'Bilingue (italiano + originale)',
                      onClick: () => handleDownload('bilingual'),
                    },
                  ]
                : undefined
            }
            busy={pdfBusy}
            disabled={!id}
          />
          {pdfBusy ? (
            <span className="text-sm" style={{ color: 'var(--mute)' }}>
              Genero PDF…
            </span>
          ) : null}
          {pdfError ? (
            <span className="text-sm" style={{ color: 'var(--danger)' }}>
              {pdfError}
            </span>
          ) : null}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* SINISTRA: TRASCRIPT */}
        <section
          className="rounded-md border p-4"
          style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
        >
          <h3 className="mono mb-3 text-xs" style={{ color: 'var(--mute)' }}>
            Trascritto
          </h3>
          <div className="space-y-2">
            {messages.map((m) => {
              const msgLike = {
                content: m.content ?? '',
                content_translation_it: m.content_translation_it ?? null,
              };
              const main = pickMainText(msgLike, encounterLang);
              const original = pickOriginalText(msgLike, encounterLang);
              const missing = needsTranslationButMissing(msgLike, encounterLang);
              const isUserBubble = m.role === 'user';

              return (
                <div
                  key={`${m.turn}-${m.role}`}
                  className={`flex ${isUserBubble ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className="max-w-[85%] rounded-2xl px-3 py-2 text-sm"
                    style={{
                      background: isUserBubble ? 'var(--primary)' : 'var(--surface)',
                      color: isUserBubble ? '#fff' : 'var(--ink)',
                      border: isUserBubble ? 'none' : '1px solid var(--line)',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    <div className="mono mb-1 text-[10px] opacity-60">
                      turn {m.turn}
                      {m.llm_tokens ? ` · ${m.llm_tokens.input}+${m.llm_tokens.output}t` : ''}
                    </div>
                    <div>{main}</div>
                    {showOriginal && original ? (
                      <OriginalText
                        text={original}
                        langCode={encounterLang}
                        inverse={isUserBubble}
                      />
                    ) : null}
                    {missing ? <MissingTranslationBadge inverse={isUserBubble} /> : null}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* DESTRA: DATI STRUTTURATI */}
        <section className="space-y-4">
          {/* Captured data per tag */}
          <DogEarCard
            className="rounded-md border p-4"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          >
            <h3 className="mono mb-3 text-xs" style={{ color: 'var(--mute)' }}>
              Dati raccolti ({captured_data.length})
            </h3>
            {captured_data.length === 0 ? (
              <p className="text-sm" style={{ color: 'var(--mute)' }}>
                Nessuna risposta strutturata catturata.
              </p>
            ) : (
              [...capturedByTag.entries()].map(([tag, items]) => (
                <div key={tag} className="mb-3">
                  <div className="mono mb-1 text-[10px] uppercase" style={{ color: 'var(--mute)' }}>
                    {tag}
                  </div>
                  <ul className="space-y-1">
                    {items.map((c) => {
                      const valueStr =
                        typeof c.captured_value === 'object'
                          ? JSON.stringify(c.captured_value)
                          : String(c.captured_value);
                      const translationText = c.captured_value_translation_it?.text ?? null;
                      const capLike = {
                        content: valueStr,
                        content_translation_it: translationText,
                      };
                      const main = pickMainText(capLike, encounterLang);
                      const original = pickOriginalText(capLike, encounterLang);
                      const missing = needsTranslationButMissing(capLike, encounterLang);

                      return (
                        <li key={c.id} className="text-sm">
                          <span style={{ color: 'var(--mute)' }}>{c.question_objective}:</span>{' '}
                          <span className="font-semibold" style={{ color: 'var(--ink)' }}>
                            {main}
                          </span>
                          <span className="mono ms-2 text-[10px]" style={{ color: 'var(--mute)' }}>
                            ({c.confidence})
                          </span>
                          {showOriginal && original ? (
                            <div className="mt-1">
                              <OriginalText text={original} langCode={encounterLang} />
                            </div>
                          ) : null}
                          {missing ? (
                            <div className="mt-1">
                              <MissingTranslationBadge />
                            </div>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))
            )}
          </DogEarCard>

          {/* Flag events */}
          <div
            className="rounded-md border p-4"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          >
            <h3 className="mono mb-3 text-xs" style={{ color: 'var(--mute)' }}>
              Flag scattati ({flag_events.length})
            </h3>
            {flag_events.length === 0 ? (
              <p className="text-sm" style={{ color: 'var(--mute)' }}>
                Nessun flag.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {flag_events.map((f) => (
                  <li key={f.id} className="flex items-center gap-2 text-sm">
                    <span
                      className="mono rounded-full px-2 py-0.5 text-[10px] uppercase"
                      style={{
                        background:
                          f.severity === 'critical'
                            ? 'var(--danger)'
                            : f.severity === 'warning'
                              ? 'var(--accent)'
                              : 'var(--mute)',
                        color: f.severity === 'warning' ? 'var(--ink)' : '#fff',
                      }}
                    >
                      {f.severity}
                    </span>
                    <span style={{ color: 'var(--ink)' }}>{f.flag_name}</span>
                    {f.trigger_keyword ? (
                      <span className="mono text-xs" style={{ color: 'var(--mute)' }}>
                        ({f.trigger_keyword})
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Outcomes */}
          <div
            className="rounded-md border p-4"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          >
            <h3 className="mono mb-3 text-xs" style={{ color: 'var(--mute)' }}>
              Esiti presentati ({outcome_results.length})
            </h3>
            {outcome_results.length === 0 ? (
              <p className="text-sm" style={{ color: 'var(--mute)' }}>
                Nessun esito (encounter non ancora completato o nessuna condizione matchata).
              </p>
            ) : (
              <ul className="space-y-2">
                {outcome_results.map((o) => (
                  <li key={o.id}>
                    <div className="font-semibold" style={{ color: 'var(--ink)' }}>
                      {o.name}
                    </div>
                    {o.description ? (
                      <div className="text-xs" style={{ color: 'var(--mute)' }}>
                        {o.description}
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Token / metrics */}
          <div
            className="rounded-md border p-4"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          >
            <h3 className="mono mb-3 text-xs" style={{ color: 'var(--mute)' }}>
              Metriche LLM
            </h3>
            <dl className="grid grid-cols-2 gap-y-1 mono text-xs" style={{ color: 'var(--ink)' }}>
              <dt style={{ color: 'var(--mute)' }}>Input tokens</dt>
              <dd>{encounter.token_stats.input}</dd>
              <dt style={{ color: 'var(--mute)' }}>Output tokens</dt>
              <dd>{encounter.token_stats.output}</dd>
              <dt style={{ color: 'var(--mute)' }}>Cache read</dt>
              <dd>{encounter.token_stats.cached}</dd>
              <dt style={{ color: 'var(--mute)' }}>Pickup code</dt>
              <dd>{encounter.pickup_code}</dd>
            </dl>
          </div>

          {/* Note encounter (Sprint 4) */}
          <DogEarCard
            className="rounded-md border p-4"
            style={{ background: 'var(--paper)', borderColor: 'var(--line)' }}
          >
            <h3 className="mono mb-2 text-xs" style={{ color: 'var(--mute)' }}>
              Note operatore su questo encounter
            </h3>
            <textarea
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                setNotesDirty(true);
              }}
              rows={4}
              placeholder="Note specifiche su questo incontro."
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={{
                background: 'var(--surface)',
                borderColor: 'var(--line)',
                color: 'var(--ink)',
              }}
            />
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                disabled={!notesDirty || notesMut.isPending}
                onClick={() => notesMut.mutate()}
                className="rounded-md px-3 py-1 text-xs font-semibold disabled:opacity-50"
                style={{ background: 'var(--primary)', color: '#fff' }}
              >
                {notesMut.isPending ? 'Salvo…' : 'Salva note'}
              </button>
            </div>
          </DogEarCard>
        </section>
      </div>
    </div>
  );
}
